import type { EmailArchiveBrowseControllerListEmailsParams } from '@/apis/backend/generated/models';

import {
  EmailArchiveBrowseControllerListEmailsSort,
  EmailArchiveBrowseControllerListEmailsIntent,
  EmailArchiveBrowseControllerListEmailsLayoutArchetype,
} from '@/apis/backend/generated/models';

// The Inspo surface keeps every choice a person makes in the query string, so a
// search is a link someone can paste and the back button undoes a filter. This
// file is the one place the string form and the request form meet.

export const INSPO_TABS = ['discover', 'brands', 'following', 'saved', 'requests'] as const;
export type InspoTab = (typeof INSPO_TABS)[number];

export const DATE_RANGES = ['7', '30', '90'] as const;
export type DateRange = (typeof DATE_RANGES)[number];

export type EmailSort = EmailArchiveBrowseControllerListEmailsSort;
export type EmailIntent = EmailArchiveBrowseControllerListEmailsIntent;
export type EmailLayout = EmailArchiveBrowseControllerListEmailsLayoutArchetype;

/** The quality floor the "high quality only" toggle applies. */
export const HIGH_QUALITY_FLOOR = 0.7;

/** Filters an email feed can carry in the URL. */
export interface EmailFilterSearch {
  q?: string;
  intent?: EmailIntent;
  layout?: EmailLayout;
  theme?: string;
  industry?: string;
  category?: string;
  esp?: string;
  date?: DateRange;
  hq?: boolean;
  following?: boolean;
  sort?: EmailSort;
}

/** Opens the email detail over whatever page carries it. */
export interface EmailDetailSearch {
  email?: string;
}

export interface InspoSearch extends EmailFilterSearch, EmailDetailSearch {
  tab?: InspoTab;
}

export const FILTER_KEYS = [
  'intent',
  'layout',
  'theme',
  'industry',
  'category',
  'esp',
  'date',
  'hq',
  'following',
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

function str(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function flag(value: unknown): true | undefined {
  return value === true || value === 'true' || value === 1 || value === '1' ? true : undefined;
}

// Unknown values are dropped rather than rejected: a stale or hand-edited link
// should still open the page, just without the filter it no longer understands.
export function parseEmailFilterSearch(search: Record<string, unknown>): EmailFilterSearch {
  return {
    q: str(search.q),
    intent: oneOf(search.intent, Object.values(EmailArchiveBrowseControllerListEmailsIntent)),
    layout: oneOf(
      search.layout,
      Object.values(EmailArchiveBrowseControllerListEmailsLayoutArchetype)
    ),
    theme: str(search.theme),
    industry: str(search.industry),
    category: str(search.category),
    esp: str(search.esp),
    date: oneOf(search.date, DATE_RANGES),
    hq: flag(search.hq),
    following: flag(search.following),
    sort: oneOf(search.sort, Object.values(EmailArchiveBrowseControllerListEmailsSort)),
  };
}

export function parseEmailDetailSearch(search: Record<string, unknown>): EmailDetailSearch {
  return { email: str(search.email) };
}

export function parseInspoSearch(search: Record<string, unknown>): InspoSearch {
  return {
    tab: oneOf(search.tab, INSPO_TABS),
    ...parseEmailFilterSearch(search),
    ...parseEmailDetailSearch(search),
  };
}

// Day-aligned so the date filter is a stable query key: a cutoff computed to
// the millisecond would mint a new key, and a refetch, on every render.
function daysAgoIso(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

/** The request a set of URL filters stands for. */
export function toEmailListParams(
  search: EmailFilterSearch,
  scope: { brandSlug?: string; followingOnly?: boolean } = {}
): EmailArchiveBrowseControllerListEmailsParams {
  const params: EmailArchiveBrowseControllerListEmailsParams = {};
  if (search.q) params.q = search.q;
  if (scope.brandSlug) params.brandSlug = scope.brandSlug;
  if (scope.followingOnly || search.following) params.followingOnly = true;
  if (search.intent) params.intent = search.intent;
  if (search.layout) params.layoutArchetype = search.layout;
  if (search.theme) params.theme = search.theme;
  if (search.industry) params.industry = search.industry;
  if (search.category) params.category = search.category;
  if (search.esp) params.esp = search.esp;
  if (search.date) params.sentAfter = daysAgoIso(Number(search.date));
  if (search.hq) params.minQuality = HIGH_QUALITY_FLOOR;
  // Relevance means nothing without a query, so it falls back to the server's
  // default rather than being sent.
  if (search.sort && !(search.sort === 'relevance' && !search.q)) params.sort = search.sort;
  return params;
}

export function activeFilterCount(search: EmailFilterSearch): number {
  return FILTER_KEYS.filter((key) => search[key] !== undefined).length;
}

/** Turns a stored vocabulary value (snake or kebab case) into a readable label. */
export function humanize(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
